import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEye,
  faPencil,
  faTrash,
  faClipboardList,
  faSpinner,
  faFileArrowUp,
} from '@fortawesome/free-solid-svg-icons';
import { equipmentIntakeService, type EquipmentIntake } from '@/services/equipment-intake.service';
import { inspectionService } from '@/services/inspection.service';
import { EditAddressesModal } from './components/EditAddressesModal';
import { IntakeDetails } from './components/IntakeDetails';
import { InwardReportPanel } from './components/InwardReportPanel';
import { InwardOperationalSummary } from './components/InwardOperationalSummary';
import { equipmentService } from '@/services/equipment.service';
import type { EquipmentWorkflowGroup } from '@/services/equipment.service';
import { IntakeForm } from './IntakeForm';
import { initialFromIntake, type IntakeFileState, type IntakeFormData } from './intake-form.state';
import type { ShipmentState } from './components/ShipmentCard';
import { SkeletonPage } from '@/components/skeletons';
import { PageHeader } from '@/components/shared/PageHeader';
import { SearchBar } from '@/components/shared/SearchBar';
import { EmptyState } from '@/components/shared/EmptyState';
import { Pagination } from '@/components/shared/Pagination';
import { Modal } from '@/components/shared/Modal';
import { Button } from '@/components/ui/button';
import { toast, confirmDelete } from '@/lib/swal';
import { useDebounce } from '@/hooks/use-debounce';
import { useHasPermission } from '@/hooks/use-permissions';
import { ROUTES } from '@/routes/routes';

const PAGE_SIZE = 20;

/**
 * D-106 — Equipment Inward is now the operational view, so it carries the
 * workflow scope that used to live on the Equipment page. Both values are
 * D-102's derived classification; nothing is computed here.
 */
const WORKFLOW_FILTERS: ReadonlyArray<{ value: EquipmentWorkflowGroup | ''; label: string }> = [
  { value: '', label: 'All workflows' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'COMPLETED', label: 'Completed' },
];

const WORKFLOW_GROUP_BADGE: Record<string, string> = {
  PENDING: 'bg-slate-500/10 text-slate-600',
  IN_PROGRESS: 'bg-amber-500/10 text-amber-700',
  COMPLETED: 'bg-green-500/10 text-green-700',
};

const FILTER_SELECT_CLASS =
  'h-9 rounded-lg border border-border bg-background px-3 text-sm text-foreground ' +
  'focus:outline-none focus:ring-2 focus:ring-ring';

function extractApiMessage(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

function fmtDate(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleDateString();
}

// ── Types ─────────────────────────────────────────────────────────────────────
// AccessoryRow, PhotoEntry, ShipmentState are now imported from ./components/*
// (extracted so the Equipment Intake Workspace, Feature 5, can reuse them
// without duplication — see docs/OPS_EQUIPMENT_INTAKE_WORKSPACE_DESIGN.md §4).

/**
 * The preview shell.
 *
 * All rendering lives in IntakeDetails — the one read-only workspace every
 * intake uses, whatever created it. This component supplies the modal frame
 * and makes sure the record inside it is the DETAIL record: the row handed
 * over by the list comes from the lean list query and carries no registered
 * address, brand, model, category or receiving lot. The list row renders
 * immediately so the modal opens with something on screen, then the detail
 * read replaces it.
 */
function IntakeDetailModal({
  open,
  intake,
  onClose,
  onEditAddresses,
}: {
  open: boolean;
  intake: EquipmentIntake;
  onClose: () => void;
  onEditAddresses?: (intake: EquipmentIntake) => void;
}) {
  const { data: detail } = useQuery({
    queryKey: ['equipment-intake-detail', intake.id],
    queryFn: () => equipmentIntakeService.get(intake.id),
    enabled: open,
  });

  return (
    <Modal open={open} title={`Inward — ${intake.intakeNumber}`} onClose={onClose} size="2xl">
      <IntakeDetails intake={detail ?? intake} onEditAddresses={onEditAddresses} />
      <div className="flex justify-end pt-3">
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
}

/**
 * The edit shell.
 *
 * Edit ALWAYS reads the intake fresh before hydrating the form. It used to
 * seed from the row the list already had, which meant a change made anywhere
 * else — another tab, another user, a direct API call — was invisible here, and
 * saving quietly wrote the stale value back over it.
 *
 * `staleTime: 0` + `refetchOnMount: 'always'` guarantee a network read every
 * time the modal opens, and the form is not mounted until that read lands. The
 * gate is `isFetching`, not just `data`: React Query hands back a cached row
 * synchronously while it revalidates, and rendering that row — even for one
 * frame — is exactly the stale-value flash this fix exists to remove.
 *
 * The list row is used for one thing only: the intake number in the title, so
 * the modal has a heading before the read completes.
 */
function IntakeEditModal({
  intakeId,
  intakeNumber,
  onClose,
  onSubmit,
  saving,
}: {
  intakeId: string;
  intakeNumber: string;
  onClose: () => void;
  onSubmit: (formData: IntakeFormData, fileState: IntakeFileState) => void;
  saving: boolean;
}) {
  const { data, isFetching, isError, error } = useQuery({
    queryKey: ['equipment-intake-detail', intakeId],
    queryFn: () => equipmentIntakeService.get(intakeId),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const ready = !isFetching && !!data;

  return (
    <Modal open onClose={onClose} title={`Edit Inward — ${intakeNumber}`} size="lg">
      {ready ? (
        <IntakeForm
          mode="edit"
          initial={initialFromIntake(data)}
          onSubmit={onSubmit}
          onCancel={onClose}
          saving={saving}
        />
      ) : isError ? (
        <div className="py-10 text-center">
          <p className="text-sm text-destructive">
            {extractApiMessage(error as Error, 'Failed to load intake details')}
          </p>
          <Button variant="ghost" className="mt-3" onClick={onClose}>
            Close
          </Button>
        </div>
      ) : (
        <div className="flex items-center justify-center gap-3 py-12 text-sm text-muted-foreground">
          <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" />
          Loading intake details...
        </div>
      )}
    </Modal>
  );
}

export function EquipmentIntakePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /**
   * D-106 — filter state lives in the URL, matching the Stage C/D convention on
   * Equipment. This is what makes the Equipment → Equipment Inward drill-down
   * (`?customerId=…`) land on a scoped view instead of a dead end.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const customerId = searchParams.get('customerId') ?? '';
  const workflowGroup = (searchParams.get('workflow') ?? '') as EquipmentWorkflowGroup | '';

  /**
   * Options for the customer filter come from the SAME `GET /equipment`
   * customerSummary the cards use — no separate customer fetch, and no
   * paginated client-side lookup (the D-101 defect).
   */
  const { data: equipmentScope } = useQuery({
    queryKey: ['inward-customer-options', workflowGroup],
    queryFn: () =>
      equipmentService.list({
        page: 1,
        pageSize: 1,
        workflowGroup: workflowGroup || undefined,
      }),
  });
  const customerOptions = (equipmentScope?.customerSummary ?? []).filter(
    (r) => r.customerId !== null,
  );

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
    setPage(1);
  };
  const can = useHasPermission();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const [editTarget, setEditTarget] = useState<EquipmentIntake | null>(null);
  const [detailTarget, setDetailTarget] = useState<EquipmentIntake | null>(null);
  const [addressEditTarget, setAddressEditTarget] = useState<EquipmentIntake | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['equipment-intake', page, debouncedSearch, customerId],
    queryFn: () =>
      equipmentIntakeService.list({
        page,
        pageSize: PAGE_SIZE,
        search: debouncedSearch || undefined,
        // D-106 — the EXISTING GET /equipment-intake customer filter. No new
        // endpoint and no second filtering mechanism.
        customerId: customerId || undefined,
      }),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['equipment-intake'] });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      formData,
      fileState,
    }: {
      id: string;
      formData: IntakeFormData;
      fileState: IntakeFileState;
    }) => {
      // STEP 1 — intakeDate, customerId and equipmentId are CREATE-ONLY and are
      // absent from UpdateEquipmentIntakeDto. With forbidNonWhitelisted the
      // server rejects a request that names them, so sending them would fail the
      // whole save; the client agrees with the server rather than testing it.
      let intake = await equipmentIntakeService.update(id, {
        customerLocationId: formData.customerLocationId || undefined,
        customerContactId: formData.customerContactId || undefined,
        complaintDescription: formData.complaintDescription,
        receivedConditionJson: formData.receivedConditionJson,
        remarks: formData.remarks || undefined,
        accessories: formData.accessories.length > 0 ? formData.accessories : undefined,
        // STEP 2 — assignedEngineerId is NOT sent to the intake. The Inspection
        // owns engineer assignment; the intake column stays as legacy
        // compatibility data and is deliberately left untouched rather than
        // synchronised, because two writers with no shared transaction is what
        // produced the divergence in the first place.
        shipments: formData.shipments,
      });

      const sortedPhotoRemovals = [...fileState.photoRemovedIndices].sort((a, b) => b - a);
      for (const idx of sortedPhotoRemovals) {
        intake = await equipmentIntakeService.deletePhoto(id, idx);
      }

      for (const file of fileState.photoPendingFiles) {
        intake = await equipmentIntakeService.uploadPhoto(id, file);
      }

      const existingShipmentIds = new Set(
        fileState.shipmentStates
          .filter((s: ShipmentState) => s.id)
          .map((s: ShipmentState) => s.id!),
      );

      for (const shState of fileState.shipmentStates) {
        if (!shState.id) continue;
        const sortedRemovals = [...shState.removedAttachmentIndices].sort((a, b) => b - a);
        for (const idx of sortedRemovals) {
          intake = await equipmentIntakeService.deleteShipmentAttachment(id, shState.id, idx);
        }
        if (shState.pendingAttachmentFiles.length > 0) {
          const shipment = intake.shipments.find((s) => s.id === shState.id);
          if (shipment) {
            for (const file of shState.pendingAttachmentFiles) {
              intake = await equipmentIntakeService.uploadShipmentAttachment(id, shipment.id, file);
            }
          }
        }
      }

      const newShipmentStates = fileState.shipmentStates.filter((s: ShipmentState) => !s.id);
      const newShipmentsInResponse = intake.shipments.filter((s) => !existingShipmentIds.has(s.id));
      for (let i = 0; i < newShipmentStates.length; i++) {
        const shState = newShipmentStates[i];
        const shipment = newShipmentsInResponse[i];
        if (!shState || !shipment || shState.pendingAttachmentFiles.length === 0) continue;
        for (const file of shState.pendingAttachmentFiles) {
          intake = await equipmentIntakeService.uploadShipmentAttachment(id, shipment.id, file);
        }
      }

      // ── Priority — a separate resource, a separate request ──────────────────
      // Written through the inspection's own endpoint, never smuggled into the
      // intake payload. Deliberately last and deliberately non-fatal: the
      // intake edit has already succeeded by this point, and failing the whole
      // save because the caller may not hold inspection:update would throw away
      // work they were entitled to do. The form only offers the control to
      // holders of that permission, so reaching the catch means the server
      // disagreed — which it is entitled to do.
      try {
        const list = await inspectionService.list({ intakeId: id, pageSize: 1 });
        const target = list.data?.[0];
        if (target) {
          // Only the fields that actually moved. Sending an unchanged value
          // would stamp assignment audit columns for an assignment nobody made.
          const patch: { priorityId?: string; assignedEngineerId?: string } = {};
          if ((target.priorityId ?? '') !== formData.priorityId) {
            patch.priorityId = formData.priorityId || undefined;
          }
          if ((target.assignedEngineerId ?? '') !== formData.assignedEngineerId) {
            patch.assignedEngineerId = formData.assignedEngineerId || undefined;
          }
          if (Object.keys(patch).length > 0) await inspectionService.update(target.id, patch);
        }
      } catch (error) {
        toast.error(
          extractApiMessage(error as Error, 'Inward saved, but inspection fields were not updated'),
        );
      }

      return intake;
    },
    onSuccess: () => {
      toast.success('Inward updated');
      setEditTarget(null);
      invalidate();
    },
    onError: (error: Error) => toast.error(extractApiMessage(error, 'Failed to update intake')),
  });

  const deleteMutation = useMutation({
    mutationFn: equipmentIntakeService.remove,
    onSuccess: () => {
      toast.success('Inward deleted');
      invalidate();
    },
    onError: (error: Error) => toast.error(extractApiMessage(error, 'Failed to delete intake')),
  });

  const handleDelete = async (intake: EquipmentIntake) => {
    const confirmed = await confirmDelete(intake.intakeNumber);
    if (confirmed) deleteMutation.mutate(intake.id);
  };

  if (isLoading) return <SkeletonPage />;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Equipment Inward"
        description="Record equipment received for service"
        onAdd={can('intake:create') ? () => navigate(ROUTES.OPS_INTAKE_WORKSPACE) : undefined}
        addLabel="New Inward"
        secondaryAction={
          can('bulk-import:create') ? (
            <Button
              variant="secondary"
              className="min-h-[44px]"
              onClick={() => navigate(ROUTES.OPS_BULK_IMPORT_NEW)}
            >
              <FontAwesomeIcon icon={faFileArrowUp} className="mr-2 h-3.5 w-3.5" />
              Bulk Upload
            </Button>
          ) : undefined
        }
      />

      <SearchBar
        value={search}
        onChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        placeholder="Search by inward #, asset #, serial #, customer, or complaint…"
      />

      {/*
        D-106 - the operational summary, moved here from Equipment. The counts
        are EQUIPMENT-based (approved decision A(a)) and reuse the existing
        GET /equipment aggregation; the heading says "Equipment Summary" so the
        number is never mistaken for a count of the inward rows below.
      */}
      <InwardOperationalSummary
        customerId={customerId}
        workflowGroup={workflowGroup}
        onSelectCustomer={(id) => setFilter('customerId', id)}
      />

      {/* D-106 - the operational scope for the list below. */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          aria-label="Customer Organisation"
          className={FILTER_SELECT_CLASS}
          value={customerId}
          onChange={(e) => setFilter('customerId', e.target.value)}
        >
          <option value="">All customer organisations</option>
          {customerOptions.map((c) => (
            <option key={c.customerId ?? ''} value={c.customerId ?? ''}>
              {c.customerOrganizationName ?? c.customerName} ({c.total})
            </option>
          ))}
        </select>

        <select
          aria-label="Workflow"
          className={FILTER_SELECT_CLASS}
          value={workflowGroup}
          onChange={(e) => setFilter('workflow', e.target.value)}
        >
          {WORKFLOW_FILTERS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        {customerId || workflowGroup ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setSearchParams(new URLSearchParams(), { replace: true });
              setPage(1);
            }}
          >
            Clear Filters
          </Button>
        ) : null}
      </div>

      {/*
        D-105 (inward correction) - the reporting workflow belongs beside the
        inward list, because the report is about equipment RECEIVED. It carries
        its own scope (period + lot) and does not filter the list above it.
      */}
      <InwardReportPanel />

      {data && data.total > 0 ? (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[820px] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left">
                  <th className="px-4 py-3 font-medium text-foreground">Inward #</th>
                  <th className="px-4 py-3 font-medium text-foreground">Date</th>
                  <th className="px-4 py-3 font-medium text-foreground">Customer Organisation</th>
                  <th className="px-4 py-3 font-medium text-foreground">Equipment</th>
                  <th className="px-4 py-3 font-medium text-foreground">Received By</th>
                  <th className="px-4 py-3 font-medium text-foreground">Workflow Stage</th>
                  <th className="px-4 py-3 font-medium text-foreground">Accessories</th>
                  <th className="px-4 py-3 font-medium text-foreground sr-only">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.items.map((intake) => {
                  const rec = intake.accessories.filter((a) => a.status === 'RECEIVED').length;
                  const mis = intake.accessories.filter((a) => a.status === 'MISSING').length;
                  const dam = intake.accessories.filter((a) => a.status === 'DAMAGED').length;
                  return (
                    <tr key={intake.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 font-mono font-medium text-primary">
                        {intake.intakeNumber}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {fmtDate(intake.intakeDate)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-0.5">
                          {/* Legal organization name, falling back to the working
                              display name when a customer has no legal name on
                              record — so no existing row loses its label. */}
                          <span>
                            {intake.customerOrganizationName ?? intake.customerName ?? (
                              <span className="text-muted-foreground italic">Company</span>
                            )}
                          </span>
                          {intake.customerLocationName && (
                            <span className="text-xs text-muted-foreground">
                              {intake.customerLocationName}
                            </span>
                          )}
                        </div>
                      </td>
                      {/* Asset number and serial number were already on the wire
                          and were the two identifiers an operator needs to match
                          a machine on the bench against a row on the screen. */}
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-foreground">{intake.equipmentName}</span>
                          <span className="text-xs text-muted-foreground">
                            Asset:{' '}
                            <span className="font-mono">{intake.equipmentAssetNumber || '—'}</span>
                          </span>
                          <span className="text-xs text-muted-foreground">
                            SN:{' '}
                            <span className="font-mono">{intake.equipmentSerialNumber || '—'}</span>
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {intake.receivedByName ?? '—'}
                      </td>
                      <td className="px-4 py-3">
                        {/*
                          D-106 - the equipment's CURRENT workflow stage, derived
                          by D-102 and resolved server-side in one batched call
                          for the page. The group is not shown as a second
                          status: it drives the tint, the filter and the summary.
                        */}
                        {intake.workflowStage ? (
                          <span
                            className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                              WORKFLOW_GROUP_BADGE[intake.workflowGroup ?? 'PENDING']
                            }`}
                          >
                            {intake.workflowStage}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">Not yet in service</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {intake.accessories.length === 0 ? (
                          <span className="text-muted-foreground text-xs">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            <span className="rounded-full bg-green-500/10 px-2 py-0.5 text-xs text-green-700">
                              {rec}
                            </span>
                            {mis > 0 && (
                              <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-xs text-amber-700">
                                {mis} miss
                              </span>
                            )}
                            {dam > 0 && (
                              <span className="rounded-full bg-red-500/10 px-2 py-0.5 text-xs text-red-700">
                                {dam} dmg
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setDetailTarget(intake)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                            title="View details"
                          >
                            <FontAwesomeIcon icon={faEye} className="h-3.5 w-3.5" />
                          </button>
                          {can('intake:update') && (
                            <button
                              onClick={() => setEditTarget(intake)}
                              className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                              title="Edit"
                            >
                              <FontAwesomeIcon icon={faPencil} className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {can('intake:delete') && (
                            <button
                              onClick={() => handleDelete(intake)}
                              disabled={deleteMutation.isPending}
                              className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
                              title="Delete"
                            >
                              <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPageChange={setPage}
          />
        </>
      ) : (
        <EmptyState
          icon={faClipboardList}
          title={debouncedSearch ? 'No inward records match your search' : 'No inward records yet'}
          description={
            debouncedSearch
              ? 'Try a different search term.'
              : 'Start by recording a new equipment inward.'
          }
          action={
            !debouncedSearch && can('intake:create') ? (
              <Button onClick={() => navigate(ROUTES.OPS_INTAKE_WORKSPACE)}>New Inward</Button>
            ) : undefined
          }
        />
      )}

      {editTarget && (
        <IntakeEditModal
          intakeId={editTarget.id}
          intakeNumber={editTarget.intakeNumber}
          onClose={() => setEditTarget(null)}
          onSubmit={(formData, fileState) =>
            updateMutation.mutate({ id: editTarget.id, formData, fileState })
          }
          saving={updateMutation.isPending}
        />
      )}

      {detailTarget && (
        <IntakeDetailModal
          open={!!detailTarget}
          intake={detailTarget}
          onClose={() => setDetailTarget(null)}
          onEditAddresses={(i) => setAddressEditTarget(i)}
        />
      )}

      {addressEditTarget && (
        <EditAddressesModal
          open={!!addressEditTarget}
          intake={addressEditTarget}
          onClose={() => setAddressEditTarget(null)}
          onSaved={(updated) => {
            // Keep the open detail view in sync with the newly frozen snapshot.
            setDetailTarget((cur) => (cur && cur.id === updated.id ? updated : cur));
          }}
        />
      )}
    </div>
  );
}
